import { defineStore } from 'pinia'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { uniqueArray } from '@/utilities'
import QueuesData from '@/api/reports/queues-data.api'
import { aiAgentApi } from '@bvai/api'

const defaultFilter = {
  queues: [],
  agents: [],
  from: '',
  to: '',
  timezoneArea: {},
  timezone: '',
  reset_at: ''
}

export const useQueuesDataStore = defineStore('queuesData', {
  state: () => ({
    _queues: [],
    _aiAgents: [],
    _uiFlags: {
      isFetching: false,
      isFetched: false,
      isAiAgentFetching: false,
      isAiAgentFetched: false
    },
    _filterData: { ...defaultFilter },
    fetchQueuesPromise: null,
    _serverTimezone: null,
    _payload: null
  }),
  getters: {
    queues(state) {
      return state._queues
    },
    aiAgents(state) {
      return state._aiAgents
    },
    agents(state) {
      return uniqueArray(state._queues.map((queue) => queue.agents).flat(), 'number')
    },
    selectedQueuesAgents(state) {
      return uniqueArray(state.selectedQueues.map((queue) => queue.agents).flat(), 'number')
    },
    selectedQueues(state) {
      return state.queues.filter((queue) => state._filterData.queues.includes(queue.number))
    },
    inboundQueues(state) {
      return state.queues.filter((queue) => queue.type === 'inbound')
    },
    outboundQueues(state) {
      return state.queues.filter((queue) => queue.type === 'outbound')
    },
    unselectedQueues(state) {
      return state.inboundQueues.filter((queue) => !state._filterData.queues.includes(queue.number))
    },
    selectedAgents(state) {
      return state.selectedQueuesAgents.filter((agent) =>
        state._filterData.agents.includes(agent.number)
      )
    },
    unselectedAgents(state) {
      return state.selectedQueuesAgents.filter(
        (agent) => !state._filterData.agents.includes(agent.number)
      )
    },
    serverTimezone(state) {
      return state._serverTimezone
    },
    payloadSelectedQueues(state) {
      return state.queues.filter((queue) => state._payload.queues.includes(queue.number))
    },
    filter(state) {
      return cloneDeep(state._filter)
    },
    payload(state) {
      const data = cloneDeep(state._payload || {})
      delete data.timezoneArea
      delete data.reset_at
      return data
    },
    reportResetAt(state) {
      return cloneDeep(state._payload?.reset_at)
    },
    reportTimezoneArea(state) {
      return cloneDeep(state._payload?.timezoneArea)
    },
    uiFlags(state) {
      return state._uiFlags
    }
  },
  actions: {
    async getQueuesData(force = false) {
      if (this.fetchQueuesPromise) {
        return this.fetchQueuesPromise
      }

      if (this.uiFlags.isFetched && !force) {
        return Promise.resolve(this._queues)
      }

      this._uiFlags.isFetching = true
      this._queues = []
      this._serverTimezone = null
      this.fetchQueuesPromise = QueuesData.get()
        .then((response) => {
          this._queues = cloneDeep(response.data.data)
          this._serverTimezone = response.data.meta?.timezone
          this.uiFlags.isFetched = true
        })
        .finally(() => {
          this._uiFlags.isFetching = false
          this.fetchQueuesPromise = null
        })
      return this.fetchQueuesPromise
    },
    async getAiAgentsData(force = false) {
      if (this.uiFlags.isAiAgentFetched && !force) {
        return Promise.resolve(this._aiAgents)
      }
      if (this.fetchAiAgentsPromise) {
        return this.fetchAiAgentsPromise
      }
      this._uiFlags.isAiAgentFetching = true
      this.fetchAiAgentsPromise = aiAgentApi
        .list()
        .then((agents) => {
          this._aiAgents = agents
          this.uiFlags.isAiAgentFetched = true
        })
        .finally(() => {
          this._uiFlags.isAiAgentFetching = false
          this.fetchAiAgentsPromise = null
        })
      return this.fetchAiAgentsPromise
    },
    getQueuesAgents(queuesNumbers) {
      const queues = this.queues.filter((queue) => {
        if (Array.isArray(queuesNumbers)) {
          return queuesNumbers.includes(queue.number)
        } else if (['number', 'string'].includes(typeof queuesNumbers)) {
          return queue.number === `${queuesNumbers}`
        }
        return true
      })
      return uniqueArray(queues.map((queue) => queue.agents).flat(), 'number')
    },
    selectQueue(queueNumber) {
      if (this._filterData.queues.indexOf(queueNumber) === -1) {
        this._filterData.queues.push(queueNumber)
      }
    },
    selectAllQueues() {
      this._filterData.queues = this.inboundQueues.map((queue) => queue.number)
    },
    unselectQueue(queueNumber) {
      const index = this._filterData.queues.indexOf(queueNumber)
      if (index !== -1) {
        this._filterData.queues.splice(index, 1)
        const selectedAgentsNumbers = this.selectedAgents.map((agent) => agent.number)
        this._filterData.agents = this._filterData.agents.filter((agent) =>
          selectedAgentsNumbers.includes(agent)
        )
      }
    },
    unselectAllQueues() {
      this._filterData.queues = []
      this._filterData.agents = []
    },
    selectAgent(agentNumber) {
      if (this._filterData.agents.indexOf(agentNumber) === -1) {
        this._filterData.agents.push(agentNumber)
      }
    },
    selectAllAgents() {
      this._filterData.agents = this.agents.map((agent) => agent.number)
    },
    unselectAgent(agentNumber) {
      const index = this._filterData.agents.indexOf(agentNumber)
      if (index !== -1) {
        this._filterData.agents.splice(index, 1)
      }
    },
    unselectAllAgents() {
      this._filterData.agents = []
    },
    setFrom(from) {
      this._filterData.from = from
    },
    setTo(to) {
      this._filterData.to = to
    },
    setTimeZoneArea(timeZoneArea) {
      this._filterData.timezoneArea = timeZoneArea
    },
    setTimeZone(timeZone) {
      this._filterData.timezone = timeZone
    },
    savePayload() {
      this._filterData.reset_at = moment().endOf('day').toDate().getTime()
      this._payload = cloneDeep(this._filterData)
      sessionStorage.setItem('report-filter', JSON.stringify(this._payload))
    },
    clearPayload() {
      this._filterData = { ...defaultFilter }
      this._payload = null
      sessionStorage.removeItem('report-filter')
    },
    loadPayload() {
      const data = JSON.parse(sessionStorage.getItem('report-filter'))
      if (!!data) {
        this._filterData = cloneDeep(data)
        this._payload = cloneDeep(data)
      }

      if (!isNaN(this._payload?.reset_at)) {
        const time = +this._payload?.reset_at - new Date().getTime()
        if (time < 0) {
          this.clearPayload()
        }
      }
    },
    async fetchQueuesDataIfNotFetched() {
      if (this.fetchQueuesPromise) {
        return this.fetchQueuesPromise
      }
      return this.getQueuesData()
    },
    async fetchAiAgentsDataIfNotFetched() {
      if (this.fetchAiAgentsPromise) {
        return this.fetchAiAgentsPromise
      }
      return this.getAiAgentsData()
    }
  }
})
